/**
 * dedupr.cloud — the public marketing site (ADR-055).
 *
 * Routing is hash-based, same rationale as apps/settings/App.tsx: this app
 * is served as static assets by Workers Assets (15 §2.1), so a hash route
 * needs no server-side rewrite rule. Three screens exist — the landing page,
 * a two-article documentation section (duplicate detection, connectors), and
 * the full pricing page — matching the approved visual prototype's design
 * language (see src/styles/tokens.css for why this app's design is
 * intentionally not @dedupr/tokens/@dedupr/ui).
 */
import { useCallback, useEffect, useState } from 'react';
import { Landing } from './pages/Landing.js';
import { Docs, type DocsArticle } from './pages/Docs.js';
import { PricingPage } from './pages/PricingPage.js';
import { AskDedupr } from './components/AskDedupr.js';
import { VideoModal } from './components/VideoModal.js';
import { BookingModal } from './components/BookingModal.js';
import { AuditRequestModal, type AuditInterest } from './components/AuditRequestModal.js';
import { useCurrency } from './hooks/useCurrency.js';
import './styles/tokens.css';

type View = 'home' | 'docs' | 'pricing';

function routeFromHash(): { view: View; article: DocsArticle } {
  const hash = window.location.hash;
  if (hash === '#/docs/connectors') return { view: 'docs', article: 'connectors' };
  if (hash === '#/docs') return { view: 'docs', article: 'duplicates' };
  if (hash === '#/pricing') return { view: 'pricing', article: 'duplicates' };
  return { view: 'home', article: 'duplicates' };
}

/** The section id in a bare hash like `#systems`, or null for a route hash. */
function sectionFromHash(hash: string): string | null {
  const id = hash.replace(/^#/, '');
  return id && !id.startsWith('/') ? id : null;
}

export function App() {
  const [{ view, article }, setRoute] = useState(() => routeFromHash());
  const [helpOpen, setHelpOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditInterest, setAuditInterest] = useState<AuditInterest>('audit');
  const [pendingSection, setPendingSection] = useState<string | null>(null);
  const currency = useCurrency();

  useEffect(() => {
    const onHashChange = () => {
      const next = routeFromHash();
      setRoute(next);
      const section = sectionFromHash(window.location.hash);
      setPendingSection(next.view === 'home' ? section : null);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  /**
   * A bare section hash (`#systems`, `#modes`, `#pricing`) resolves to the home
   * view, so following one from the docs or pricing screen is a route change and
   * a scroll at once. The browser attempts its own scroll-to-fragment at
   * hashchange time — before React has rendered the view holding that element —
   * so it finds nothing and you land silently at the top of the home page.
   *
   * This has to be an effect rather than a `requestAnimationFrame` inside the
   * listener: the frame callback also runs before the commit, so it misses the
   * element for exactly the same reason. An effect is the first point at which
   * the new view is in the DOM.
   *
   * The jump is deliberately instant — no `behavior: 'smooth'`. Smooth is the
   * nicer gesture within a page, but this is a route change, and a smooth
   * request is one a user agent may legitimately decline: measured no-opping
   * outright here, leaving the reader at the top of a page they did not ask for.
   * An instant landing always happens, and after a screen change it is what a
   * reader expects anyway.
   */
  useEffect(() => {
    if (view !== 'home' || !pendingSection) return;
    document.getElementById(pendingSection)?.scrollIntoView({ block: 'start' });
    setPendingSection(null);
  }, [view, pendingSection]);

  const goHome = useCallback(() => {
    window.location.hash = '#/';
    setRoute({ view: 'home', article: 'duplicates' });
    setVideoOpen(false);
    window.scrollTo(0, 0);
  }, []);

  const goDocs = useCallback((target: DocsArticle = 'duplicates') => {
    window.location.hash = target === 'connectors' ? '#/docs/connectors' : '#/docs';
    setRoute({ view: 'docs', article: target });
    setVideoOpen(false);
    window.scrollTo(0, 0);
  }, []);

  const goPricing = useCallback(() => {
    window.location.hash = '#/pricing';
    setRoute({ view: 'pricing', article: 'duplicates' });
    setVideoOpen(false);
    window.scrollTo(0, 0);
  }, []);

  const goDocsHome = useCallback(() => goDocs('duplicates'), [goDocs]);
  const goConnectorsDocs = useCallback(() => goDocs('connectors'), [goDocs]);

  const toggleHelp = useCallback(() => setHelpOpen((open) => !open), []);
  const openVideo = useCallback(() => setVideoOpen(true), []);
  const closeVideo = useCallback(() => setVideoOpen(false), []);
  const openBooking = useCallback(() => setBookingOpen(true), []);
  const closeBooking = useCallback(() => setBookingOpen(false), []);

  // One form, two intents. "Free data audit" asks us to run the findings against
  // their systems; "Get the local sandbox" asks for the local-first build. Both
  // POST to the Worker's /api/audit-request route (worker/index.ts) and go out
  // via Resend, with `interest` telling whoever picks it up which follow-up the
  // person is expecting.
  const openAudit = useCallback(() => {
    setAuditInterest('audit');
    setAuditOpen(true);
  }, []);
  const openSandbox = useCallback(() => {
    setAuditInterest('sandbox');
    setAuditOpen(true);
  }, []);
  const closeAudit = useCallback(() => setAuditOpen(false), []);

  return (
    <>
      {view === 'home' ? (
        <Landing
          currency={currency}
          onGoDocs={goDocsHome}
          onGoConnectorsDocs={goConnectorsDocs}
          onGoHome={goHome}
          onGoPricing={goPricing}
          onOpenVideo={openVideo}
          onOpenAudit={openAudit}
          onOpenSandbox={openSandbox}
          onOpenBooking={openBooking}
          onToggleHelp={toggleHelp}
        />
      ) : view === 'pricing' ? (
        <PricingPage
          currency={currency}
          onGoHome={goHome}
          onGoDocs={goDocsHome}
          onGoPricing={goPricing}
          onOpenAudit={openAudit}
          onOpenBooking={openBooking}
          onToggleHelp={toggleHelp}
        />
      ) : (
        <Docs
          article={article}
          onNavigate={goDocs}
          ccyCode={currency.code}
          regionLabel={currency.regionLabel}
          onCycleCurrency={currency.cycle}
          onGoHome={goHome}
          onToggleHelp={toggleHelp}
        />
      )}
      <AskDedupr open={helpOpen} onToggle={toggleHelp} onGoDocs={goDocsHome} onOpenBooking={openBooking} />
      <VideoModal open={videoOpen} onClose={closeVideo} onGoDocs={goDocsHome} />
      <BookingModal open={bookingOpen} onClose={closeBooking} />
      <AuditRequestModal open={auditOpen} interest={auditInterest} onClose={closeAudit} />
    </>
  );
}
